import { Component, Inject, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { AddBoardMemberSearchComponent } from '../add-board-member-search/add-board-member-search.component';
import { DirectorFormComponent } from '../../../../../directors/director-form/director-form.component';
import { DirectorFlat } from '../../../../../../models/director';
import { DirectorService } from '../../../../../../services/director.service';
import { DirectorPositionsResponseDto } from '../../../../../../models/api/directorPositionsDto';
import { InfoDialogService } from '../../../../../../services/info-dialog.service';
import { switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export interface AddBoardMemberDialogData {
  targetType: 'board' | 'management' | 'executive';
  boardType: number; // 0=unitary, 1=supervisory, 2=management
}

export interface AddBoardMemberDialogResult {
  action: 'connect' | 'created';
  director: DirectorFlat;
  targetType: 'board' | 'management' | 'executive';
}

interface UpdateResult {
  success: boolean;
  photoUploaded?: boolean;
  photoDeleted?: boolean;
  photoError?: any;
  photoDeleteError?: any;
}

type DialogMode = 'add-new' | 'search-connect' | 'edit';

@Component({
  selector: 'app-add-board-member-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    NgxSpinnerModule,
    AddBoardMemberSearchComponent,
    DirectorFormComponent
  ],
  templateUrl: './add-board-member-dialog.component.html',
  styleUrls: ['./add-board-member-dialog.component.scss']
})
export class AddBoardMemberDialogComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private directorService = inject(DirectorService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private infoDialogService = inject(InfoDialogService);

  @ViewChild(DirectorFormComponent) directorFormComponent?: DirectorFormComponent;

  currentMode: DialogMode = 'search-connect';
  selectedDirector: DirectorFlat | null = null;

  // Edit mode state
  editDirectorId: string | null = null;
  editDirectorName: string | null = null;
  editPositions: DirectorPositionsResponseDto | null = null;
  private loadedPhotoUrl: string | null = null;

  // Form for creating a new director
  createForm: FormGroup = this.fb.group({
    fullName: ['', Validators.required],
    yearOfBirth: ['', [Validators.min(1900), Validators.max(new Date().getFullYear())]],
    yearOfBirthConfirmed: [false],
    age: [''],
    gender: [0],
    honorificTitle: [''],
    nationality: [''],
    secondNationality: [''],
    isDeceased: [false],
    dateOfDeath: [''],
    cvSummary: [''],
    photoFileName: [''],
    profession: [''],
    industryExpertise1: [''],
    industryExpertise2: [''],
    qualification: [''],
    hasFinancialExpertise: [false],
    financialExpertise: [''],
    hasPoliticalPosition: [false],
    politicalPosition: [''],
    isCompleted: [false],
    dateCompleted: [''],
    isActive: [true],
    workExperiences: [[]],
    trainingAndEducations: [[]]
  });

  // Form for editing an existing director
  editForm: FormGroup = this.fb.group({
    fullName: ['', Validators.required],
    yearOfBirth: ['', [Validators.min(1900), Validators.max(new Date().getFullYear())]],
    yearOfBirthConfirmed: [false],
    age: [''],
    gender: [''],
    honorificTitle: [''],
    nationality: [''],
    secondNationality: [''],
    isDeceased: [false],
    dateOfDeath: [''],
    cvSummary: [''],
    photoFileName: [''],
    profession: [''],
    industryExpertise1: [''],
    industryExpertise2: [''],
    qualification: [''],
    hasFinancialExpertise: [false],
    financialExpertise: [''],
    hasPoliticalPosition: [false],
    politicalPosition: [''],
    isCompleted: [false],
    dateCompleted: [''],
    isActive: [true],
    workExperiences: [[]],
    trainingAndEducations: [[]]
  });

  constructor(
    public dialogRef: MatDialogRef<AddBoardMemberDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddBoardMemberDialogData
  ) {}

  ngOnDestroy(): void {
    if (this.loadedPhotoUrl) {
      URL.revokeObjectURL(this.loadedPhotoUrl);
    }
  }

  getDialogTitle(): string {
    if (this.currentMode === 'add-new') {
      return 'Create New Director';
    }
    if (this.currentMode === 'edit') {
      return `Edit Director — ${this.editDirectorName || ''}`;
    }

    switch (this.data.targetType) {
      case 'board':
        return this.data.boardType === 0
          ? 'Add Director to Board of Directors'
          : 'Add Director to Supervisory Board';
      case 'management':
        return 'Add Director to Management Board';
      case 'executive':
        return 'Add Director to Executive Management';
      default:
        return 'Add Board Member';
    }
  }

  onBack(): void {
    this.currentMode = 'search-connect';
    this.selectedDirector = null;
    this.cleanupEditState();
  }

  onAddNewFromSearch(): void {
    this.currentMode = 'add-new';
  }

  onEditDirector(director: DirectorFlat): void {
    this.editDirectorId = director.id?.toString() || null;
    this.editDirectorName = director.fullName;
    this.currentMode = 'edit';
    this.loadDirectorForEdit(this.editDirectorId!);
  }

  onDirectorSelected(director: DirectorFlat | null): void {
    this.selectedDirector = director;
  }

  onCreateSubmit(): void {
    if (!this.createForm.valid) return;

    const formValue = this.createForm.value;

    const createRequest = {
      fullName: formValue.fullName,
      yearOfBirth: formValue.yearOfBirth,
      yearOfBirthConfirmed: formValue.yearOfBirthConfirmed,
      honorificTitleId: formValue.honorificTitle?.id || formValue.honorificTitle,
      gender: formValue.gender,
      nationalityId: formValue.nationality?.id || formValue.nationality,
      secondNationalityId: formValue.secondNationality?.id || formValue.secondNationality || null,
      isDeceased: formValue.isDeceased,
      dateOfDeath: formValue.dateOfDeath,
      professionId: formValue.profession?.id || formValue.profession,
      industryExpertise1Id: formValue.industryExpertise1?.id || formValue.industryExpertise1,
      industryExpertise2Id: formValue.industryExpertise2?.id || formValue.industryExpertise2 || null,
      qualificationId: formValue.qualification?.id || formValue.qualification,
      hasFinancialExpertise: formValue.hasFinancialExpertise,
      financialExpertiseId: formValue.financialExpertise?.id || formValue.financialExpertise || null,
      hasPoliticalPosition: formValue.hasPoliticalPosition,
      politicalPositionId: formValue.politicalPosition?.id || formValue.politicalPosition || null,
      cvSummary: formValue.cvSummary,
      workExperience: this.transformWorkExperienceToFlat(formValue.workExperiences || []),
      trainingOrEducation: this.transformTrainingAndEducationToFlat(formValue.trainingAndEducations || [])
    };

    this.spinnerService.show();
    this.directorService.createDirector(createRequest).pipe(
      switchMap((createdDirector) => {
        const photoFile = this.directorFormComponent?.getPhotoFromForm();
        if (photoFile && createdDirector.id) {
          return this.directorService.uploadDirectorPhoto(createdDirector.id, photoFile).pipe(
            switchMap(() => of({ director: createdDirector, photoUploaded: true })),
            catchError(() => of({ director: createdDirector, photoUploaded: false }))
          );
        }
        return of({ director: createdDirector, photoUploaded: false });
      }),
      catchError((error) => {
        console.error('Error creating director:', error);
        this.spinnerService.hide();
        this.toast.error('Error creating director');
        return of(null);
      })
    ).subscribe((result) => {
      this.spinnerService.hide();
      if (!result) return;

      this.toast.success('Director created successfully');
      const dialogResult: AddBoardMemberDialogResult = {
        action: 'created',
        director: result.director,
        targetType: this.data.targetType
      };
      this.dialogRef.close(dialogResult);
    });
  }

  onEditSubmit(): void {
    if (!this.editForm.valid || !this.editDirectorId) return;

    const formValue = this.editForm.value;

    const updateRequest = {
      fullName: formValue.fullName,
      yearOfBirth: formValue.yearOfBirth,
      yearOfBirthConfirmed: formValue.yearOfBirthConfirmed,
      honorificTitleId: formValue.honorificTitle?.id || formValue.honorificTitle || null,
      gender: formValue.gender,
      nationalityId: formValue.nationality?.id || formValue.nationality,
      secondNationalityId: formValue.secondNationality?.id || formValue.secondNationality || null,
      isDeceased: formValue.isDeceased,
      dateOfDeath: formValue.dateOfDeath,
      professionId: formValue.profession?.id || formValue.profession || null,
      industryExpertise1Id: formValue.industryExpertise1?.id || formValue.industryExpertise1 || null,
      industryExpertise2Id: formValue.industryExpertise2?.id || formValue.industryExpertise2 || null,
      qualificationId: formValue.qualification?.id || formValue.qualification || null,
      hasFinancialExpertise: formValue.hasFinancialExpertise,
      financialExpertiseId: formValue.financialExpertise?.id || formValue.financialExpertise || null,
      hasPoliticalPosition: formValue.hasPoliticalPosition,
      politicalPositionId: formValue.politicalPosition?.id || formValue.politicalPosition || null,
      isCompleted: formValue.isCompleted,
      dateCompleted: formValue.dateCompleted,
      cvSummary: formValue.cvSummary,
      workExperience: this.transformWorkExperienceToFlat(formValue.workExperiences || [], this.editDirectorId),
      trainingOrEducation: this.transformTrainingAndEducationToFlat(formValue.trainingAndEducations || [], this.editDirectorId)
    };

    this.spinnerService.show();
    this.directorService.updateDirector(this.editDirectorId, updateRequest).pipe(
      switchMap(() => {
        const photoWasRemoved = this.directorFormComponent?.wasPhotoRemoved();
        const photoFile = this.directorFormComponent?.getPhotoFromForm();

        if (photoWasRemoved && this.editDirectorId) {
          return this.directorService.deleteDirectorPhoto(this.editDirectorId).pipe(
            switchMap(() => {
              if (photoFile && this.editDirectorId) {
                return this.directorService.uploadDirectorPhoto(this.editDirectorId, photoFile).pipe(
                  switchMap(() => of<UpdateResult>({ success: true, photoDeleted: true, photoUploaded: true })),
                  catchError((error) => of<UpdateResult>({ success: true, photoDeleted: true, photoUploaded: false, photoError: error }))
                );
              }
              return of<UpdateResult>({ success: true, photoDeleted: true, photoUploaded: false });
            }),
            catchError((error) => of<UpdateResult>({ success: true, photoDeleted: false, photoDeleteError: error }))
          );
        } else if (photoFile && this.editDirectorId) {
          return this.directorService.uploadDirectorPhoto(this.editDirectorId, photoFile).pipe(
            switchMap(() => of<UpdateResult>({ success: true, photoUploaded: true })),
            catchError((error) => of<UpdateResult>({ success: true, photoUploaded: false, photoError: error }))
          );
        }
        return of<UpdateResult>({ success: true, photoUploaded: false });
      }),
      catchError((error) => {
        console.error('Error updating director:', error);
        this.spinnerService.hide();
        this.infoDialogService.openErrorDialog(
          'Failed to update director. Please try again.',
          'Update Error'
        );
        return of<UpdateResult>({ success: false });
      })
    ).subscribe((result) => {
      this.spinnerService.hide();
      if (!result.success) return;

      this.directorFormComponent?.resetPhotoRemovedFlag();
      this.toast.success('Director updated successfully');
      this.editForm.markAsPristine();
    });
  }

  onConfirmConnect(): void {
    if (!this.selectedDirector) return;
    const result: AddBoardMemberDialogResult = {
      action: 'connect',
      director: this.selectedDirector,
      targetType: this.data.targetType
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private loadDirectorForEdit(id: string): void {
    this.spinnerService.show();
    this.directorService.getDirectorById(id).subscribe({
      next: (director) => {
        if (director) {
          const transformedWorkExperiences = this.transformWorkExperience(director.workExperience || []);
          const transformedTrainingAndEducations = this.transformTrainingAndEducation(director.trainingOrEducation || []);

          const formValue = {
            ...director,
            honorificTitle: director.honorificTitleId ? { id: director.honorificTitleId, name: director.honorificTitleName } : null,
            nationality: director.nationalityId ? { id: director.nationalityId, name: director.nationalityName } : null,
            secondNationality: director.secondNationalityId ? { id: director.secondNationalityId, name: director.secondNationalityName } : null,
            profession: director.professionId ? { id: director.professionId, name: director.professionName } : null,
            industryExpertise1: director.industryExpertise1Id ? { id: director.industryExpertise1Id, name: director.industryExpertise1Name } : null,
            industryExpertise2: director.industryExpertise2Id ? { id: director.industryExpertise2Id, name: director.industryExpertise2Name } : null,
            qualification: director.qualificationId ? { id: director.qualificationId, name: director.qualificationName } : null,
            financialExpertise: director.financialExpertiseId ? { id: director.financialExpertiseId, name: director.financialExpertiseName } : null,
            politicalPosition: director.politicalPositionId ? { id: director.politicalPositionId, name: director.politicalPositionName } : null,
            workExperiences: transformedWorkExperiences,
            trainingAndEducations: transformedTrainingAndEducations
          };

          this.editDirectorName = director.fullName;
          this.editForm.patchValue(formValue, { emitEvent: false });

          setTimeout(() => {
            if (this.directorFormComponent) {
              this.directorFormComponent.updateAgeFieldState();
              this.directorFormComponent.workExperiences = transformedWorkExperiences;
              this.directorFormComponent.trainingAndEducations = transformedTrainingAndEducations;
            }
          }, 0);

          if (director.photoFileName) {
            this.loadDirectorPhoto(id);
          }

          this.loadDirectorPositions(id);
        }
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading director:', error);
        this.spinnerService.hide();
        this.toast.error('Error loading director');
        this.currentMode = 'search-connect';
      }
    });
  }

  private loadDirectorPhoto(id: string): void {
    this.directorService.getDirectorPhoto(id).subscribe({
      next: (photoBlob) => {
        if (photoBlob && photoBlob.size > 0) {
          if (this.loadedPhotoUrl) {
            URL.revokeObjectURL(this.loadedPhotoUrl);
          }
          const photoUrl = URL.createObjectURL(photoBlob);
          this.loadedPhotoUrl = photoUrl;

          setTimeout(() => {
            if (this.directorFormComponent) {
              this.directorFormComponent.existingPhotoUrl = photoUrl;
              this.directorFormComponent.selectedPhotoName = 'Current Photo';
            }
          }, 0);
        }
      },
      error: () => {}
    });
  }

  private loadDirectorPositions(id: string): void {
    this.directorService.getDirectorPositions(id).subscribe({
      next: (positions) => {
        this.editPositions = positions;
      },
      error: (error) => {
        console.error('Error loading director positions:', error);
      }
    });
  }

  private cleanupEditState(): void {
    this.editDirectorId = null;
    this.editDirectorName = null;
    this.editPositions = null;
    this.editForm.reset({
      gender: '',
      isDeceased: false,
      hasFinancialExpertise: false,
      hasPoliticalPosition: false,
      isCompleted: false,
      isActive: true,
      workExperiences: [],
      trainingAndEducations: []
    });
    if (this.loadedPhotoUrl) {
      URL.revokeObjectURL(this.loadedPhotoUrl);
      this.loadedPhotoUrl = null;
    }
  }

  private transformWorkExperience(workExperienceFlat: any[]): any[] {
    return workExperienceFlat.map(we => {
      const { company, companyType } = this.getCompanyFromWorkExperience(we);
      return {
        id: we.id,
        company,
        companyType,
        position: we.jobPositionId ? { id: we.jobPositionId, name: we.jobPositionName } : null,
        startDate: we.from,
        endDate: we.until
      };
    });
  }

  private getCompanyFromWorkExperience(we: any): { company: any; companyType: string } {
    if (we.opbCompanyId) {
      return { company: { id: we.opbCompanyId, name: we.opbCompanyName }, companyType: 'opbCompany' };
    } else if (we.companyId) {
      return { company: { id: we.companyId, name: we.companyName }, companyType: 'company' };
    } else if (we.workExperienceCompanyId) {
      return { company: { id: we.workExperienceCompanyId, name: we.workExperienceCompanyName }, companyType: 'workExperienceCompany' };
    } else if (we.auditFirmId) {
      return { company: { id: we.auditFirmId, name: we.auditFirmName }, companyType: 'auditFirm' };
    } else if (we.largeShareholderId) {
      return { company: { id: we.largeShareholderId, name: we.largeShareholderName }, companyType: 'largeShareholder' };
    }
    return { company: null, companyType: '' };
  }

  private transformTrainingAndEducation(trainingFlat: any[]): any[] {
    return trainingFlat.map(te => ({
      id: te.id,
      type: te.type,
      educationField: te.educationFieldId ? { id: te.educationFieldId, name: te.educationFieldName } : null,
      university: te.universityId ? { id: te.universityId, name: te.universityName } : null
    }));
  }

  private transformWorkExperienceToFlat(workExperiences: any[], directorId?: string): any[] {
    return workExperiences.map(we => {
      const flatWe: any = {
        id: we.id,
        companyId: null,
        companyName: null,
        opbCompanyId: null,
        opbCompanyName: null,
        workExperienceCompanyId: null,
        workExperienceCompanyName: null,
        auditFirmId: null,
        auditFirmName: null,
        largeShareholderId: null,
        largeShareholderName: null,
        jobPositionId: we.position?.id || null,
        jobPositionName: we.position?.name || null,
        from: we.startDate || null,
        until: we.endDate || null
      };

      if (directorId) {
        flatWe.directorId = directorId;
      }

      if (we.company && we.companyType) {
        switch (we.companyType) {
          case 'opbCompany':
            flatWe.opbCompanyId = we.company.id;
            flatWe.opbCompanyName = we.company.name;
            break;
          case 'company':
            flatWe.companyId = we.company.id;
            flatWe.companyName = we.company.name;
            break;
          case 'workExperienceCompany':
            flatWe.workExperienceCompanyId = we.company.id;
            flatWe.workExperienceCompanyName = we.company.name;
            break;
          case 'auditFirm':
            flatWe.auditFirmId = we.company.id;
            flatWe.auditFirmName = we.company.name;
            break;
          case 'largeShareholder':
            flatWe.largeShareholderId = we.company.id;
            flatWe.largeShareholderName = we.company.name;
            break;
        }
      }

      return flatWe;
    });
  }

  private transformTrainingAndEducationToFlat(trainingAndEducations: any[], directorId?: string): any[] {
    return trainingAndEducations.map(te => ({
      id: te.id,
      ...(directorId && { directorId }),
      educationFieldId: te.educationField?.id || null,
      educationFieldName: te.educationField?.name || null,
      universityId: te.university?.id || null,
      universityName: te.university?.name || null,
      type: te.type
    }));
  }
}
